import React from 'react';
import { graphql, useFragment, type PreloadedQuery } from 'react-relay';
import Heading from '@atlaskit/heading/heading';
import { useIntl, defineMessage } from '@atlassian/jira-intl';
import { useRelayResource } from '@jira/platform__graphql-relay-utils/src/services/resources/index.tsx';
import { nav4SidebarSettingsPageSwitcherResource } from '@jira/router-resources__navigation-sidebar-settings-page-switcher/src/nav4SidebarSettingsPageSwitcherResource.tsx';
import { useSettingsSidebarPermissionRef } from '../../common/ui/settingsSidebarPermissionQuery.tsx';
import type { settingsSidebarPermissionQuery } from '../../common/ui/__generated__/settingsSidebarPermissionQuery.graphql';
import type { SidebarHeading_jira$key } from './__generated__/SidebarHeading_jira.graphql';

const heading = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-settings-global-common.sidebar-content.heading',
	defaultMessage: 'Jira admin settings',
	description: 'Heading for the global jira settings pages',
});

const jsmAppAdminHeading = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-settings-global-common.sidebar-content.jsm-app-admin-heading',
	defaultMessage: 'App settings',
	description:
		'Title shown at the top of the global settings sidebar to JSM-only app admins, matching the settings cog menu wording. Shown instead of "Jira admin settings".',
});

const HeadingView = ({ isJsmAppAdmin }: { isJsmAppAdmin: boolean }): React.JSX.Element => {
	const { formatMessage } = useIntl();

	return (
		<Heading size="small" as="h2">
			{formatMessage(isJsmAppAdmin ? jsmAppAdminHeading : heading)}
		</Heading>
	);
};

const SidebarHeadingWithData = ({
	queryReference,
}: {
	queryReference: PreloadedQuery<settingsSidebarPermissionQuery>;
}): React.JSX.Element => {
	const jiraFragment = useSettingsSidebarPermissionRef(queryReference);
	const jiraData = useFragment<SidebarHeading_jira$key>(
		graphql`
			fragment SidebarHeading_jira on JiraQuery {
				hasJsmAdminAccess(cloudId: $cloudId) @optIn(to: "JiraHasJsmAdminAccess")
			}
		`,
		jiraFragment,
	);

	return <HeadingView isJsmAppAdmin={jiraData?.hasJsmAdminAccess === true} />;
};

export const SidebarHeading = (): React.JSX.Element => {
	// `suspendWhenLoading` makes this suspend (instead of returning a null reference) while the shared
	// permission resource is still loading. Combined with the <Placeholder> boundary in SidebarContent,
	// this lets the heading resolve during SSR — matching the page switcher — so it renders the correct
	// title in the first paint instead of flickering from the default after hydration.
	const { queryReference } = useRelayResource(nav4SidebarSettingsPageSwitcherResource, {
		suspendWhenLoading: true,
	});

	if (queryReference == null) {
		return <HeadingView isJsmAppAdmin={false} />;
	}

	return <SidebarHeadingWithData queryReference={queryReference} />;
};
